/** The sheets of Season's Home: a need opened, the results, and the people behind Right now. */
import { Link } from "react-router";
import type { HomeChain, HomeNeed, HomeRightNow } from "../../../shared/types";
import { Funnel } from "../../components/season/Numbers";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import { Button } from "../../components/ui/Button";
import { Face } from "../../components/ui/Face";
import { Sheet } from "../../components/ui/Sheet";
import { fmt } from "../../lib/when";
import { chainLine } from "./seasonParts";
import { clickedLine, needWords, registeredLine, STEPS, useOpenPerson } from "./parts";

/** What was said and what it asks of the host: the quote, and the words around it. Shared by the sheet and the side pane. */
export function NeedBody({ need }: { need: HomeNeed }) {
  const w = needWords(need);
  return (
    <>
      <div className="hs-dh">
        <Face name={need.person.name} photo={need.person.avatar_url} size={56} />
        <div>
          <h2 className="hs-sn">{need.person.name}</h2>
          <p className="k-rm">{w.label}</p>
        </div>
      </div>
      <p className="hs-why">{w.why}</p>
      {need.quote ? <blockquote className="hs-q">{`“${need.quote}”`}</blockquote> : null}
    </>
  );
}

/** The one way to answer a need: its thread when it has one, else a note. */
export function AnswerButton({ need, onNote, onGo }: { need: HomeNeed; onNote: () => void; onGo?: () => void }) {
  const openPerson = useOpenPerson();
  return need.conversation_id ? (
    <Button
      variant="p"
      icon="chat"
      onClick={() => {
        openPerson(need);
        onGo?.();
      }}
    >
      Answer
    </Button>
  ) : (
    <Button variant="p" icon="send" onClick={onNote}>
      Send a note
    </Button>
  );
}

/** A need opened from its row, on a phone and on an iPad held upright. */
export function NeedSheet({ need, onClose, onDone, onNote }: { need: HomeNeed | null; onClose: () => void; onDone: () => void; onNote: () => void }) {
  if (!need) return null;
  return (
    <Sheet
      open
      onClose={onClose}
      title={need.conversation_id ? "Needs an answer" : "Needs a note"}
      label={need.person.name}
      footer={
        <>
          <Button onClick={onDone}>Mark as done</Button>
          <AnswerButton need={need} onNote={onNote} onGo={onClose} />
        </>
      }
    >
      <NeedBody need={need} />
    </Sheet>
  );
}

/** How people moved through all the rooms, step by step. */
export function ResultsSheet({ chain, open, onClose }: { chain: HomeChain; open: boolean; onClose: () => void }) {
  const on = chain.customers_connected;
  const counts = [chain.registered, chain.came, chain.reached, chain.clicked, chain.customers];
  const steps = STEPS.slice(0, on ? 5 : 4).map((s, i) => ({ label: s.label, value: counts[i], shown: fmt(counts[i]) }));
  const line = chainLine(chain);
  return (
    <Sheet open={open} onClose={onClose} title="Your results">
      <Funnel steps={steps} of={chain.registered} />
      <p className="k-rm hs-legend">
        {line.words}
        <span className="k-sep" />
        {line.end}
      </p>
      {on ? null : (
        <Link className="k-link hs-seeall" to="/settings#ingest" onClick={onClose}>
          Connect your booking app
        </Link>
      )}
    </Sheet>
  );
}

/** Who registered or clicked the offer today, with the rest left to People. */
export function PeopleSheet({ now, which, onClose }: { now: HomeRightNow; which: "registered" | "clicked" | null; onClose: () => void }) {
  if (!which) return null;
  const registered = which === "registered";
  const col = now[which];
  const rest = col.count - col.people.length;
  return (
    <Sheet open onClose={onClose} title={`${registered ? "Just registered" : "Clicked the offer"}, ${fmt(col.count)} today`}>
      <GroupedList>
        {col.people.map((p) => (
          <ListRow
            key={p.id}
            lead={<Face name={p.name} photo={p.avatar_url} size={44} />}
            title={p.name}
            sub={"offer_label" in p ? clickedLine(p) : registeredLine(p)}
            to={`/people/${p.id}`}
          />
        ))}
      </GroupedList>
      {rest > 0 ? (
        <Link className="k-link hs-seeall" to={registered ? "/people?group=new" : "/people"} onClick={onClose}>
          {`And ${fmt(rest)} more today, in People`}
        </Link>
      ) : null}
    </Sheet>
  );
}
